import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Loader2,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  Flag,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Edit3,
  Save,
  History,
  X,
  Info,
  RefreshCw,
} from "lucide-react";
import "./Sandbox.css";
import DOMPurify from "dompurify";
import { getLocalStorageWithExpiry, showApiError, showSuccess } from "@/utils";
import Spinner from "@/components/ui/Spinner";
import useGetQuery from "@/hooks/useGetQuery";
import { useQueryClient } from "@tanstack/react-query";
import {
  NEW_SESSION_MSG,
  FLAG_REASONS,
  formatSessionDate,
  toDateTimeString,
} from "./sandboxHelpers";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(
  /\/+$/,
  "",
);

export function AnswerHTML({ html, className = "" }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    // Sanitize was already applied when setting innerHTML, but double-check wrapper
    const root = ref.current;
    // Force target="_blank" and rel="noopener noreferrer" on all anchor tags
    Array.from(root.querySelectorAll("a")).forEach((a) => {
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
    });
    // Find any direct child elements that use inline flex and switch to column
    Array.from(root.querySelectorAll("[style]")).forEach((el) => {
      try {
        const s = el.getAttribute("style") || "";
        if (/display:\s*flex/.test(s)) {
          el.style.display = "flex";
          el.style.flexDirection = "column";
          // ensure children stretch full width
          el.style.alignItems = "stretch";
          // reduce gap slightly for vertical layout
          if (el.style.gap) el.style.gap = el.style.gap.replace(/16px/, "12px");
        }
      } catch (e) {
        // ignore
      }
    });
  }, [html]);

  return (
    <div
      ref={ref}
      className={`small text-dark bg-white rounded border p-2 sandbox-answer-html ${className}`.trim()}
      dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(html, {
          ALLOWED_ATTR: ["style", "href", "target", "rel"],
        }),
      }}
    />
  );
}

// ── Conversation History Drawer ───────────────────────────────────────────────
function ConversationHistoryDrawer({
  isOpen,
  onClose,
  onSelectSession,
  currentSessionId,
}) {
  const { t, i18n } = useTranslation();
  const drawerRef = useRef(null);

  const historyUrl = `${BACKEND_URL}/api/v1/answer_engine_sandbox/conversation-history`;
  const [historyData, historyLoaded, historyError] = useGetQuery(
    isOpen ? historyUrl : null,
    { staleTime: 5 * 60 * 1000 },
  );

  const conversations = historyData?.conversations || [];
  const isLoading = isOpen && !historyLoaded && !historyError;
  const error = historyError ? "Failed to load conversation history." : null;

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocused =
      document.activeElement && document.activeElement !== document.body
        ? document.activeElement
        : null;

    const timer = setTimeout(() => {
      if (drawerRef.current) {
        const focusable = drawerRef.current.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length > 0) {
          focusable[0].focus();
        } else {
          drawerRef.current.focus();
        }
      }
    }, 0);

    return () => {
      clearTimeout(timer);
      if (
        previouslyFocused &&
        typeof previouslyFocused.focus === "function" &&
        document.body.contains(previouslyFocused)
      ) {
        previouslyFocused.focus();
      }
    };
  }, [isOpen]);

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onClose();
      return;
    }

    if (e.key === "Tab" && drawerRef.current) {
      const focusable = Array.from(
        drawerRef.current.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );

      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }

      const firstElement = focusable[0];
      const lastElement = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (
          document.activeElement === firstElement ||
          !drawerRef.current.contains(document.activeElement)
        ) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (
          document.activeElement === lastElement ||
          !drawerRef.current.contains(document.activeElement)
        ) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={drawerRef}
      className="history-drawer history-drawer--open"
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label={t("answerEngine.conversationHistory", "Conversation History")}
      onKeyDown={handleKeyDown}
    >
      <div className="px-3 py-2 border-bottom bg-white d-flex align-items-center justify-content-between">
        <div>
          <h6 className="fw-bold text-dark mb-0">
            {t("answerEngine.conversationHistory", "Conversation History")}
          </h6>
          <p className="small text-muted mb-0">
            {t(
              "answerEngine.selectConversationToReview",
              "Select a conversation to review",
            )}
          </p>
        </div>
        <button
          onClick={onClose}
          className="btn btn-link text-muted p-1"
          aria-label={t("answerEngine.closeHistory", "Close history")}
        >
          <X size={18} />
        </button>
      </div>

      <div className="flex-grow-1 overflow-auto">
        {isLoading && (
          <div className="text-center py-5">
            <Spinner />
          </div>
        )}
        {error && (
          <div className="text-center py-4 px-3">
            <p className="small text-danger mb-0">{error}</p>
          </div>
        )}
        {!isLoading && !error && conversations.length === 0 && (
          <div className="text-center py-5 px-3">
            <History className="mb-2 text-muted" size={32} />
            <p className="small text-muted mb-0">
              {t(
                "answerEngine.noPastConversations",
                "No past conversations found.",
              )}
            </p>
          </div>
        )}
        <div className="list-group list-group-flush">
          {conversations.map((conversation) => {
            const isActive = conversation.session_id === currentSessionId;
            return (
              <button
                key={conversation.session_id}
                onClick={() => onSelectSession(conversation.session_id)}
                className={`list-group-item list-group-item-action text-start p-3 border-0 ${
                  isActive ? "active bg-light fw-bold" : ""
                }`}
              >
                <p className="mb-1 small text-dark text-truncate-2">
                  {conversation.first_question ||
                    t(
                      "answerEngine.untitledConversation",
                      "Untitled conversation",
                    )}
                </p>
                <div className="d-flex justify-content-between align-items-center mt-1">
                  <span
                    className="small text-muted"
                    style={{ fontSize: "0.75rem" }}
                  >
                    {formatSessionDate(conversation.created_at, i18n.resolvedLanguage || i18n.language)}
                    {conversation.interaction_count > 0
                      ? ` · ${t("answerEngine.questionsCount", {
                          count: conversation.interaction_count,
                          defaultValue: `${conversation.interaction_count} questions`,
                        })}`
                      : ""}
                  </span>
                  {isActive && (
                    <span
                      className="badge rounded-pill"
                      style={{
                        fontSize: "0.75rem",
                        background:
                          "linear-gradient(to right, #2DC6FE, #81FBEF)",
                        color: "#082D35",
                      }}
                    >
                      {t("answerEngine.current", "Current")}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const RATING_OPTIONS = [
  {
    value: "good",
    labelKey: "answerEngine.good",
    defaultLabel: "Good",
    icon: ThumbsUp,
    activeClass: "btn-success",
  },
  {
    value: "bad",
    labelKey: "answerEngine.poor",
    defaultLabel: "Poor",
    icon: ThumbsDown,
    activeClass: "btn-danger",
  },
  {
    value: "flag",
    labelKey: "answerEngine.flag",
    defaultLabel: "Flag",
    icon: Flag,
    activeClass: "btn-warning text-white",
  },
];

// ── Feedback Panel ────────────────────────────────────────────────────────────
function FeedbackPanel({
  interactions,
  onFeedbackSaved,
  onHistoryOpen,
  onReturnToLive,
  isViewingHistory,
  loadError,
  isLoading,
}) {
  const { t, i18n } = useTranslation();
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState("");
  const [rating, setRating] = useState(null); // "good" | "bad" | "flag"
  const [correction, setCorrection] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const radioRefs = useRef([]);

  const handleRadioKeyDown = (e, index) => {
    if (
      e.key === "ArrowRight" ||
      e.key === "ArrowDown" ||
      e.key === "ArrowLeft" ||
      e.key === "ArrowUp"
    ) {
      e.preventDefault();
      let nextIndex;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        nextIndex = (index + 1) % RATING_OPTIONS.length;
      } else {
        nextIndex = (index - 1 + RATING_OPTIONS.length) % RATING_OPTIONS.length;
      }
      const nextValue = RATING_OPTIONS[nextIndex].value;
      setRating(nextValue);
      setTimeout(() => {
        radioRefs.current[nextIndex]?.focus();
      }, 0);
    }
  };

  const flagReasonTranslations = {
    "Incorrect information": t(
      "answerEngine.flagReasons.incorrectInfo",
      "Incorrect information",
    ),
    "Off-brand tone": t(
      "answerEngine.flagReasons.offBrandTone",
      "Off-brand tone",
    ),
    "Mentioned a competitor": t(
      "answerEngine.flagReasons.mentionedCompetitor",
      "Mentioned a competitor",
    ),
    "Too salesy": t(
      "answerEngine.flagReasons.tooSalesy",
      "Too salesy",
    ),
    "Missing key information": t(
      "answerEngine.flagReasons.missingKeyInfo",
      "Missing key information",
    ),
    "Confusing or unclear": t(
      "answerEngine.flagReasons.confusingOrUnclear",
      "Confusing or unclear",
    ),
  };

  const saved = useMemo(() => {
    let _saved = {};
    interactions.forEach((i) => {
      if (i?.feedback?.rating) {
        _saved[i.answer_id] = i.feedback;
      }
    });
    return _saved;
  }, [interactions]);

  const handleSelect = (i) => {
    if (selected?.answer_id === i.answer_id) {
      setSelected(null);
      return;
    }
    setSelected(i);
    const existingFeedback = saved[i.answer_id] || i.feedback;
    setComment(existingFeedback?.comment || "");
    setRating(existingFeedback?.rating || null);
    setCorrection(existingFeedback?.correction || "");
  };

  const handleCancel = () => {
    const existingFeedback = (selected && saved[selected.answer_id]) || selected?.feedback;
    const hasUnsavedChanges =
      comment.trim() !== (existingFeedback?.comment || "").trim() ||
      correction.trim() !== (existingFeedback?.correction || "").trim() ||
      (rating || null) !== (existingFeedback?.rating || null);

    if (
      hasUnsavedChanges &&
      !window.confirm(
        t(
          "answerEngine.discardFeedbackConfirm",
          "You have unsaved feedback. Are you sure you want to discard your changes?",
        ),
      )
    ) {
      return;
    }
    setSelected(null);
  };

  const handleSave = async () => {
    if (!selected) return;
    if (!rating) {
      showApiError(
        t(
          "answerEngine.selectRatingAlert",
          "Please select a rating before saving.",
        ),
      );
      return;
    }
    if (rating === "flag" && !comment.trim()) {
      showApiError(
        t(
          "answerEngine.commentRequiredAlert",
          "Please add a comment when flagging an answer.",
        ),
      );
      return;
    }
    setIsSaving(true);
    try {
      const feedback = { rating, comment, correction };
      if (onFeedbackSaved) {
        await onFeedbackSaved(selected.answer_id, feedback, selected.session_id);
      }
      setSelected(null);
    } catch (e) {
      console.error("Error saving feedback:", e);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="d-flex flex-column h-100">
      <div className="px-3 py-2 border-bottom bg-white">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <h6 className="fw-bold text-dark mb-0">
              {t("answerEngine.feedbackAndTraining", "Feedback & Training")}
            </h6>
            <p className="small text-muted mb-0">
              {t(
                "answerEngine.rateAnswersToImprove",
                "Rate answers to improve the AI",
              )}
            </p>
          </div>
          <button
            onClick={onHistoryOpen}
            className={`btn btn-sm d-flex align-items-center gap-1 rounded-pill ${
              isViewingHistory ? "btn-secondary" : "btn-outline-secondary"
            }`}
            style={{ fontSize: "0.75rem" }}
            title={t(
              "answerEngine.viewConversationHistory",
              "View conversation history",
            )}
          >
            <History size={14} />
            {t("answerEngine.history", "History")}
          </button>
        </div>
        {isViewingHistory && (
          <div
            className="mt-2 d-flex align-items-center justify-content-between small rounded-pill fw-semibold shadow-sm"
            style={{
              background: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)",
              color: "#92400e",
              border: "1px solid #fcd34d",
              fontSize: "0.75rem",
              letterSpacing: "0.01em",
              boxShadow: "0 1px 3px rgba(217, 119, 6, 0.15)",
              padding: "6px 14px",
              gap: "8px",
            }}
          >
            <div className="d-flex align-items-center gap-1">
              <Info size={14} className="flex-shrink-0" style={{ color: "#d97706" }} />
              <span>
                {t(
                  "answerEngine.viewingHistoryNotice",
                  "Viewing historical conversation",
                )}
              </span>
            </div>
            {onReturnToLive && (
              <button
                type="button"
                onClick={onReturnToLive}
                className="btn btn-sm btn-link text-decoration-underline p-0 fw-bold border-0 bg-transparent ms-2"
                style={{ color: "#b45309", fontSize: "0.75rem", cursor: "pointer" }}
              >
                {t("answerEngine.resetToLiveSession", "Reset to Live Session")}
              </button>
            )}
          </div>
        )}
        {loadError && interactions.length > 0 && (
          <div
            className="mt-2 small text-danger px-1"
            style={{ fontSize: "0.75rem" }}
          >
            {loadError}
          </div>
        )}
      </div>

      <div className="flex-grow-1 overflow-auto">
        {isLoading && (
          <div className="text-center py-5">
            <Spinner />
          </div>
        )}
        {!isLoading && loadError && interactions.length === 0 && (
          <div className="text-center py-5 px-3">
            <p className="small text-danger mb-0">{loadError}</p>
          </div>
        )}
        {!isLoading && !loadError && interactions.length === 0 && (
          <div className="text-center py-5 px-3">
            <MessageSquare className="mb-2 text-muted" size={32} />
            <p className="small text-muted">
              {t("answerEngine.noInteractionsYet", "No interactions yet.")}
              <br />
              {t(
                "answerEngine.askQuestionsInEngine",
                "Ask questions in the engine to see them here.",
              )}
            </p>
          </div>
        )}
        <div className="list-group list-group-flush">
          {interactions.map((i) => {
            const fb = saved[i.answer_id];
            const isOpen = selected?.answer_id === i.answer_id;
            return (
              <div key={i.answer_id} className="list-group-item p-0">
                <button
                  onClick={() => handleSelect(i)}
                  className="w-100 text-start p-3 border-0 bg-white"
                >
                  <div className="d-flex align-items-start justify-content-between mb-1">
                    <p className="mb-0 small text-dark flex-grow-1 text-truncate-2">
                      {i.question}
                    </p>
                    <div className="d-flex align-items-center ms-2">
                      {fb &&
                        (fb.rating === "good" ? (
                          <ThumbsUp size={14} className="text-success" />
                        ) : fb.rating === "bad" ? (
                          <ThumbsDown size={14} className="text-danger" />
                        ) : fb.rating === "flag" ? (
                          <Flag size={14} className="text-warning" />
                        ) : (
                          <CheckCircle size={14} className="text-info" />
                        ))}
                      {isOpen ? (
                        <ChevronUp size={14} className="text-muted ms-1" />
                      ) : (
                        <ChevronDown size={14} className="text-muted ms-1" />
                      )}
                    </div>
                  </div>
                  <div className="d-flex gap-1 mt-1 flex-wrap">
                    {i.funnel_stage && (
                      <span
                        className={`badge rounded-pill ${
                          i.funnel_stage === "bottom"
                            ? "bg-danger"
                            : i.funnel_stage === "middle"
                              ? "bg-warning"
                              : "bg-primary"
                        }`}
                        style={{ fontSize: "0.75rem" }}
                      >
                        {i.funnel_stage === "top"
                          ? t("tracking.topFunnel", "Top")
                          : i.funnel_stage === "middle"
                            ? t("tracking.middleFunnel", "Middle")
                            : i.funnel_stage === "bottom"
                              ? t("tracking.bottomFunnel", "Bottom")
                              : i.funnel_stage}
                      </span>
                    )}
                    {i.is_branded !== null && i.is_branded !== undefined && (
                      <span
                        className={`badge rounded-pill ${
                          i.is_branded ? "bg-success" : "bg-secondary"
                        }`}
                        style={{ fontSize: "0.75rem" }}
                      >
                        {i.is_branded
                          ? t("promptLibrary.branded", "Branded")
                          : t("promptLibrary.nonBranded", "Non-Branded")}
                      </span>
                    )}
                    <span
                      className="small text-muted"
                      style={{ fontSize: "0.75rem" }}
                    >
                      {toDateTimeString(i.answer_timestamp, i18n.resolvedLanguage || i18n.language)}
                    </span>
                  </div>
                </button>

                {isOpen && (
                  <div className="p-3 bg-light border-top">
                    {i.answer && (
                      <div className="mb-3">
                        <label
                          className="sandbox-field-label fw-semibold text-muted text-uppercase mb-1 d-block"
                          style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}
                        >
                          {t("answerEngine.aiAnswer", "AI Answer")}
                        </label>
                        <AnswerHTML html={i.answer} />
                      </div>
                    )}

                    <div className="mb-3">
                      {(() => {
                        const ratingLabelId = `rating-label-${i.answer_id}`;
                        const ratingRequiredHintId = `rating-required-${i.answer_id}`;
                        return (
                          <>
                            <label
                              id={ratingLabelId}
                              className="sandbox-field-label fw-semibold text-muted text-uppercase mb-2 d-block"
                              style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}
                            >
                              {t(
                                "answerEngine.rateThisAnswer",
                                "Rate this answer",
                              )}{" "}
                              <span aria-hidden="true" className="text-danger">
                                *
                              </span>
                            </label>
                            <span
                              id={ratingRequiredHintId}
                              className="visually-hidden"
                            >
                              Required
                            </span>
                            <div
                              role="radiogroup"
                              aria-labelledby={ratingLabelId}
                              aria-describedby={ratingRequiredHintId}
                              aria-required={true}
                              className="d-flex gap-2"
                            >
                              {RATING_OPTIONS.map((opt, idx) => {
                                const isChecked = rating === opt.value;
                                const isTabbable = rating
                                  ? isChecked
                                  : idx === 0;
                                const Icon = opt.icon;
                                return (
                                  <button
                                    key={opt.value}
                                    ref={(el) => (radioRefs.current[idx] = el)}
                                    onClick={() =>
                                      setRating(opt.value)
                                    }
                                    onKeyDown={(e) =>
                                      handleRadioKeyDown(e, idx)
                                    }
                                    role="radio"
                                    aria-checked={isChecked}
                                    tabIndex={isTabbable ? 0 : -1}
                                    className={`btn btn-sm d-flex align-items-center gap-1 rounded-pill ${
                                      isChecked
                                        ? opt.activeClass
                                        : "btn-outline-secondary"
                                    }`}
                                    style={{ fontSize: "0.75rem" }}
                                  >
                                    <Icon size={12} />{" "}
                                    {t(opt.labelKey, opt.defaultLabel)}
                                  </button>
                                );
                              })}
                            </div>
                          </>
                        );
                      })()}
                    </div>

                    <div className="mb-3">
                      {(() => {
                        const commentLabelId = `comment-label-${i.answer_id}`;
                        const commentRequiredHintId = `comment-required-${i.answer_id}`;
                        const commentTextareaId = `comment-textarea-${i.answer_id}`;
                        return (
                          <>
                            <label
                              id={commentLabelId}
                              htmlFor={commentTextareaId}
                              className="sandbox-field-label fw-semibold text-muted text-uppercase mb-1 d-block"
                              style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}
                            >
                              {t("answerEngine.comment", "Comment")}{" "}
                              {rating === "flag" && (
                                <span
                                  aria-hidden="true"
                                  className="text-danger"
                                >
                                  *
                                </span>
                              )}
                            </label>
                            {rating === "flag" && (
                              <span
                                id={commentRequiredHintId}
                                className="visually-hidden"
                              >
                                Required
                              </span>
                            )}
                          </>
                        );
                      })()}
                      {rating === "flag" && (
                        <div className="d-flex flex-wrap gap-1 mb-2">
                          {FLAG_REASONS.map((reason) => (
                            <button
                              key={reason}
                              type="button"
                              onClick={() => setComment(reason)}
                              className={`btn btn-sm rounded-pill ${
                                comment === reason
                                  ? "btn-warning text-white"
                                  : "btn-outline-secondary"
                              }`}
                              style={{ fontSize: "0.75rem" }}
                            >
                              {flagReasonTranslations[reason] || reason}
                            </button>
                          ))}
                        </div>
                      )}
                      <textarea
                        id={`comment-textarea-${i.answer_id}`}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder={
                          rating === "flag"
                            ? t(
                                "answerEngine.commentPlaceholderFlag",
                                "Required — describe the issue...",
                              )
                            : t(
                                "answerEngine.commentPlaceholderDefault",
                                "What could be improved?",
                              )
                        }
                        className={`form-control form-control-sm small ${
                          rating === "flag" && !comment.trim()
                            ? "is-invalid"
                            : ""
                        }`}
                        rows="2"
                        aria-required={rating === "flag"}
                        aria-describedby={
                          rating === "flag"
                            ? `comment-required-${i.answer_id}`
                            : undefined
                        }
                        required={rating === "flag"}
                      />
                    </div>

                    <div className="mb-3">
                      <label
                        className="sandbox-field-label fw-semibold text-muted text-uppercase mb-1 d-block"
                        style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}
                      >
                        <Edit3 size={10} className="me-1" />
                        {t(
                          "answerEngine.suggestedCorrection",
                          "Suggested Correction",
                        )}
                      </label>
                      <textarea
                        value={correction}
                        onChange={(e) => setCorrection(e.target.value)}
                        placeholder={t(
                          "answerEngine.correctionPlaceholder",
                          "How should the AI have answered?",
                        )}
                        className="form-control form-control-sm small"
                        rows="2"
                      />
                    </div>

                    <div className="d-flex gap-2 align-items-center">
                      <button
                        onClick={handleSave}
                        disabled={
                          isSaving ||
                          !rating ||
                          (rating === "flag" && !comment.trim())
                        }
                        className="btn btn-primary btn-sm flex-grow-1"
                        style={{
                          background:
                            "linear-gradient(to right, #2DC6FE, #81FBEF)",
                          color: "#082D35",
                          border: "none",
                        }}
                      >
                        {isSaving ? (
                          <Loader2
                            size={12}
                            className="spinner-border spinner-border-sm border-0"
                          />
                        ) : (
                          <>
                            <Save size={12} className="me-1" />
                            {t("answerEngine.saveFeedback", "Save Feedback")}
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleCancel}
                        className="btn btn-link btn-sm text-muted text-decoration-none"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {t("common.cancel", "Cancel")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Main Sandbox Page ─────────────────────────────────────────────────────────
const MAX_SAVE_ATTEMPTS = 3;
const INITIAL_RETRY_DELAY_MS = 3000;

export default function Sandbox() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const PROJECT_ID =
    getLocalStorageWithExpiry("answer_engine_project_id") ||
    getLocalStorageWithExpiry("client_id");
  const [interactions, setInteractions] = useState([]);
  const [isInteractionsLoading, setIsInteractionsLoading] = useState(true);
  const [conversationLoadError, setConversationLoadError] = useState(null);
  const [widgetError, setWidgetError] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [, setSessionId] = useState(null);
  const [viewingSessionId, setViewingSessionId] = useState(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isViewingHistory, setIsViewingHistory] = useState(false);
  const lastAnswerIdRef = useRef(null);
  const liveSessionIdRef = useRef(null);
  const liveInteractionsRef = useRef([]);
  const isViewingHistoryRef = useRef(false);
  const isMountedRef = useRef(true);
  const savingInFlightRef = useRef(new Set());
  const saveFailedRef = useRef(new Set());
  const saveAttemptsRef = useRef(new Map());
  const [onlineTrigger, setOnlineTrigger] = useState(0);

  const handleReturnToLive = useCallback(() => {
    isViewingHistoryRef.current = false;
    setInteractions(liveInteractionsRef.current);
    setViewingSessionId(liveSessionIdRef.current);
    setIsViewingHistory(false);
    setConversationLoadError(null);
    setIsInteractionsLoading(false);
  }, []);

  const handleRetryWidget = useCallback(() => {
    setWidgetError(null);

    const scriptUrl =
      process.env.REACT_APP_AE_WIDGET_SRC ||
      "https://cdn.geo.elelem.ai/answer-engine/dev/script_latest.js";
    const projectId = getLocalStorageWithExpiry("client_id") || "elelem";
    const aeBackendUrl = process.env.REACT_APP_AE_BACKEND_URL || "/ae-backend";

    const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);
    if (existingScript) {
      existingScript.remove();
    }

    if (window.ElelemWidget) {
      try {
        if (typeof window.ElelemWidget.destroy === "function") {
          window.ElelemWidget.destroy();
        }
      } catch (err) {
        // Ignore destroy error
      }
      try {
        delete window.ElelemWidget;
      } catch (err) {
        window.ElelemWidget = undefined;
      }
    }

    const scriptEl = document.createElement("script");
    scriptEl.src = scriptUrl;
    scriptEl.setAttribute("project-id", projectId);
    scriptEl.setAttribute("data-api-base", aeBackendUrl);
    scriptEl.setAttribute("apiBase", aeBackendUrl);
    scriptEl.setAttribute("apibase", aeBackendUrl);
    scriptEl.async = true;
    scriptEl.onerror = () => {
      if (!isMountedRef.current) return;
      setWidgetError(
        t(
          "answerEngine.widgetConfigLoadError",
          "Failed to load Answer Engine widget configuration from CDN.",
        ),
      );
    };
    document.body.appendChild(scriptEl);
  }, [t]);

  useEffect(() => {
    isViewingHistoryRef.current = isViewingHistory;
  }, [isViewingHistory]);

  useEffect(() => {
    isMountedRef.current = true;
    const handleOnline = () => {
      saveFailedRef.current.clear();
      saveAttemptsRef.current.clear();
      savingInFlightRef.current.clear();
      setOnlineTrigger((prev) => prev + 1);
    };

    window.addEventListener("online", handleOnline);
    return () => {
      isMountedRef.current = false;
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  const handleInterceptedMessage = useCallback(({ chat_session_id, user_message, reply, response_id, question_timestamp, answer_timestamp }) => {
    if (!isMountedRef.current) return;
    if (response_id === lastAnswerIdRef.current) {
      console.warn("Duplicate answer_id received, skipping:", response_id);
      return;
    }
    lastAnswerIdRef.current = response_id;

    // Check if we need to switch or start a new session
    if (!liveSessionIdRef.current || liveSessionIdRef.current !== chat_session_id) {
      setSessionId(chat_session_id);
      liveSessionIdRef.current = chat_session_id;
      liveInteractionsRef.current = [];
      savingInFlightRef.current.clear();
      saveFailedRef.current.clear();
      saveAttemptsRef.current.clear();
      if (!isViewingHistoryRef.current) {
        setViewingSessionId(chat_session_id);
        setIsViewingHistory(false);
        setInteractions([]);
      }
    }

    const newEntry = {
      question: user_message,
      answer: reply,
      answer_id: response_id,
      session_id: chat_session_id,
      question_timestamp: question_timestamp || new Date().toISOString(),
      answer_timestamp: answer_timestamp || new Date().toISOString(),
      funnel_stage: null,
      is_branded: null,
      feedback: null,
      isSaved: false,
    };

    if (isViewingHistoryRef.current) {
      if (!liveInteractionsRef.current.some((i) => i.answer_id === response_id)) {
        liveInteractionsRef.current = [...liveInteractionsRef.current, newEntry];
      }
    } else {
      setInteractions((prev) => {
        if (prev.some((i) => i.answer_id === response_id)) return prev;
        const next = [...prev, newEntry];
        liveInteractionsRef.current = next;
        return next;
      });
    }

    queryClient.invalidateQueries({
      queryKey: [`${BACKEND_URL}/api/v1/answer_engine_sandbox/conversation-history`],
      exact: true,
    });
    queryClient.invalidateQueries({
      queryKey: [`${BACKEND_URL}/api/v1/answer_engine_sandbox/last-conversation`],
      exact: true,
    });
  }, [queryClient]);
  const PREVIEW_BKGD =
    getLocalStorageWithExpiry("answer_engine_sandbox_bkgd") ||
    "https://cdn.geo.elelem.ai/answer-engine/sandbox/assets/preview-bg/default.png";

  const lastConvUrl = `${BACKEND_URL}/api/v1/answer_engine_sandbox/last-conversation`;
  const [lastConvData, _lastConvLoaded, lastConvError] = useGetQuery(
    PROJECT_ID ? lastConvUrl : null,
    { staleTime: 0 },
  );

  useEffect(() => {
    if (lastConvData) {
      const conversation = lastConvData?.conversation || [];
      const session_id = lastConvData?.session_id;
      if (session_id) {
        setSessionId((prev) => (prev !== session_id ? session_id : prev));
        if (!isViewingHistoryRef.current) {
          setViewingSessionId((prev) => (prev !== session_id ? session_id : prev));
        }
        liveSessionIdRef.current = session_id;
      }
      if (conversation.length > 0) {
        const savedAnswerIds = new Set(conversation.map((c) => c.answer_id));
        const mapped = conversation.map((i) => ({ ...i, isSaved: true }));
        if (liveInteractionsRef.current.length === 0) {
          liveInteractionsRef.current = mapped;
        }
        if (!isViewingHistoryRef.current) {
          setInteractions((prev) => {
            if (prev.length === 0) return mapped;
            let hasChanges = false;
            const updated = prev.map((item) => {
              if (savedAnswerIds.has(item.answer_id) && !item.isSaved) {
                hasChanges = true;
                return { ...item, isSaved: true };
              }
              return item;
            });
            if (!hasChanges) return prev;
            liveInteractionsRef.current = updated;
            return updated;
          });
        }
      }
      setIsInteractionsLoading(false);
    } else if (lastConvError) {
      setIsInteractionsLoading(false);
      const errorMsg =
        lastConvError?.message ||
        t(
          "answerEngine.failedToLoadConversation",
          "Failed to load conversation.",
        );
      setConversationLoadError(errorMsg);
      showApiError(errorMsg);
    }
  }, [lastConvData, lastConvError, t]);

  const sessionUrl =
    viewingSessionId && isViewingHistory && viewingSessionId !== liveSessionIdRef.current
      ? `${BACKEND_URL}/api/v1/answer_engine_sandbox/conversation?session_id=${encodeURIComponent(viewingSessionId)}`
      : null;

  const [sessionResponse, _sessionLoaded, sessionError] = useGetQuery(sessionUrl, {
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (
      sessionResponse &&
      Array.isArray(sessionResponse.conversation) &&
      isViewingHistoryRef.current &&
      viewingSessionId !== liveSessionIdRef.current
    ) {
      const mapped = sessionResponse.conversation.map((i) => ({ ...i, isSaved: true }));
      setInteractions(mapped);
      setIsInteractionsLoading(false);
    } else if (sessionError && isViewingHistoryRef.current) {
      setIsInteractionsLoading(false);
      setConversationLoadError("Failed to load the selected conversation.");
      showApiError("Failed to load the selected conversation.");
    }
  }, [sessionResponse, sessionError, viewingSessionId]);

  const saveFeedback = useCallback(
    async (answer_id, feedback, targetSessionId) => {
      try {
        const activeSessionId = targetSessionId || viewingSessionId;
        if (!activeSessionId) {
          throw new Error("No session ID available for saving feedback");
        }

        const r = await fetch(
          `${BACKEND_URL}/api/v1/answer_engine_sandbox/feedback?session_id=${encodeURIComponent(activeSessionId)}`,
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ answer_id, feedback }),
          },
        );
        if (!r.ok) {
          throw new Error(`Failed to save feedback: ${r.statusText}`);
        }
        if (isMountedRef.current) {
          const updateFn = (prev) =>
            prev.map((i) => (i.answer_id === answer_id ? { ...i, feedback } : i));

          setInteractions(updateFn);
          liveInteractionsRef.current = updateFn(liveInteractionsRef.current);

          queryClient.invalidateQueries({
            queryKey: [`${BACKEND_URL}/api/v1/answer_engine_sandbox/conversation-history`],
            exact: true,
          });
          queryClient.invalidateQueries({
            queryKey: [`${BACKEND_URL}/api/v1/answer_engine_sandbox/conversation?session_id=${encodeURIComponent(activeSessionId)}`],
            exact: true,
          });

          showSuccess(
            t(
              "answerEngine.saveFeedbackSuccess",
              "Feedback saved successfully.",
            ),
          );
        }
      } catch (e) {
        console.error("Error saving feedback:", e);
        if (isMountedRef.current) {
          showApiError(
            t(
              "answerEngine.saveFeedbackError",
              "Failed to save feedback. Please try again.",
            ),
          );
        }
        throw e;
      }
    },
    [viewingSessionId, t, queryClient],
  );

  const loadConversation = useCallback((session_id) => {
    setConversationLoadError(null);
    // If the user selected the currently-live session, restore from refs directly.
    if (session_id === liveSessionIdRef.current) {
      isViewingHistoryRef.current = false;
      setInteractions(liveInteractionsRef.current);
      setViewingSessionId(liveSessionIdRef.current);
      setIsViewingHistory(false);
      setIsHistoryOpen(false);
      return;
    }
    setIsInteractionsLoading(true);
    isViewingHistoryRef.current = true;
    setViewingSessionId(session_id);
    setIsViewingHistory(true);
    setIsHistoryOpen(false);
  }, []);

  const setFunnelAndBrandedLabels = async (question) => {
    if (!question || question === NEW_SESSION_MSG) {
      return { funnel_stage: null, is_branded: null };
    }

    try {
      const r = await fetch(
        `${BACKEND_URL}/api/v1/query-management/classify-query`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ query: question }),
        },
      );
      if (!r.ok) {
        throw new Error(`Failed to classify query: ${r.statusText}`);
      }
      const json = await r.json();
      const funnel_stage = json?.funnel_stage ?? null;
      const is_branded = json?.is_branded ?? null;
      return { funnel_stage, is_branded };
    } catch (e) {
      console.error("Error classifying query:", e);
      return { funnel_stage: null, is_branded: null };
    }
  };

  const saveInteraction = useCallback(
    async (interaction) => {
      const targetSessionId = interaction.session_id || liveSessionIdRef.current;
      if (!targetSessionId) {
        console.error("No session ID, cannot save interaction");
        return;
      }

      const answerId = interaction.answer_id;
      if (!answerId) return;

      if (
        savingInFlightRef.current.has(answerId) ||
        saveFailedRef.current.has(answerId)
      ) {
        return;
      }

      savingInFlightRef.current.add(answerId);

      try {
        if (
          interaction.funnel_stage === null ||
          interaction.is_branded === null
        ) {
          const { funnel_stage, is_branded } = await setFunnelAndBrandedLabels(
            interaction.question,
          );
          if (!isMountedRef.current) return;
          interaction = { ...interaction, funnel_stage, is_branded };
        }

        if (!isMountedRef.current) return;

        // eslint-disable-next-line no-unused-vars
        const { session_id, isSaved, ...payload } = interaction;

        const r = await fetch(
          `${BACKEND_URL}/api/v1/answer_engine_sandbox/interaction?session_id=${encodeURIComponent(targetSessionId)}`,
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          },
        );
        if (!r.ok && r.status !== 409) {
          throw new Error(`Failed to save interaction: ${r.statusText}`);
        }
        interaction.isSaved = true;
        savingInFlightRef.current.delete(answerId);
        saveFailedRef.current.delete(answerId);
        saveAttemptsRef.current.delete(answerId);

        if (isMountedRef.current) {
          setInteractions((prev) =>
            prev.map((i) =>
              i.answer_id === interaction.answer_id ? interaction : i,
            ),
          );
          liveInteractionsRef.current = liveInteractionsRef.current.map((i) =>
            i.answer_id === interaction.answer_id ? interaction : i,
          );
          queryClient.invalidateQueries({
            queryKey: [`${BACKEND_URL}/api/v1/answer_engine_sandbox/conversation-history`],
            exact: true,
          });
        }
      } catch (e) {
        console.error("Error saving interaction:", e);
        savingInFlightRef.current.delete(answerId);

        const attemptInfo = saveAttemptsRef.current.get(answerId) || { count: 0 };
        const currentAttempts = attemptInfo.count + 1;
        saveAttemptsRef.current.set(answerId, {
          count: currentAttempts,
          lastAttempt: Date.now(),
        });

        if (currentAttempts >= MAX_SAVE_ATTEMPTS) {
          saveFailedRef.current.add(answerId);
        } else if (isMountedRef.current) {
          setOnlineTrigger((prev) => prev + 1);
        }
      }
    },
    [queryClient],
  );

  useEffect(() => {
    const timers = [];

    interactions.forEach((i) => {
      const answerId = i.answer_id;
      if (
        !i.isSaved &&
        answerId &&
        !savingInFlightRef.current.has(answerId) &&
        !saveFailedRef.current.has(answerId)
      ) {
        const attemptInfo = saveAttemptsRef.current.get(answerId);
        if (!attemptInfo) {
          saveInteraction(i);
        } else {
          const delay =
            INITIAL_RETRY_DELAY_MS * Math.pow(2, attemptInfo.count - 1);
          const elapsed = Date.now() - attemptInfo.lastAttempt;
          if (elapsed >= delay) {
            saveInteraction(i);
          } else {
            const timer = setTimeout(() => {
              if (isMountedRef.current) {
                saveInteraction(i);
              }
            }, delay - elapsed);
            timers.push(timer);
          }
        }
      }
    });

    return () => {
      timers.forEach((t) => clearTimeout(t));
    };
  }, [interactions, saveInteraction, onlineTrigger]);

  useEffect(() => {
    let isOpen = false;
    let overlayObserver = null;

    function attachOverlayObserver() {
      if (overlayObserver) return; // already attached
      const overlay = document.getElementById("ae-modal-overlay");
      if (!overlay) return;
      overlayObserver = new MutationObserver(() => {
        if (!overlay.classList.contains("is-open") && isOpen) {
          isOpen = false;
          setIsChatOpen(false);
        }
      });
      overlayObserver.observe(overlay, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    const handleWidgetError = () => {
      if (!isMountedRef.current) return;
      setWidgetError(
        t(
          "answerEngine.widgetConfigLoadError",
          "Failed to load Answer Engine widget configuration from CDN.",
        ),
      );
    };

    const handleWindowError = (e) => {
      const msg = e?.message || e?.error?.message || "";
      if (
        msg.includes("Widget config") ||
        msg.includes("Failed to fetch remote configuration") ||
        msg.includes("[Elelem]")
      ) {
        handleWidgetError();
      }
    };

    const handleUnhandledRejection = (e) => {
      const reason = e?.reason?.message || String(e?.reason || "");
      if (
        reason.includes("Widget config") ||
        reason.includes("Failed to fetch remote configuration") ||
        reason.includes("[Elelem]")
      ) {
        handleWidgetError();
      }
    };

    window.addEventListener("error", handleWindowError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    const originalConsoleError = console.error;
    console.error = (...args) => {
      originalConsoleError.apply(console, args);
      const msg = args
        .map((a) => {
          try {
            return typeof a === "object" ? JSON.stringify(a) : String(a);
          } catch {
            return String(a);
          }
        })
        .join(" ");
      if (
        msg.includes("Widget config request failed") ||
        msg.includes("Failed to fetch remote configuration") ||
        msg.includes("[Elelem] Failed")
      ) {
        handleWidgetError();
      }
    };

    const scriptUrl =
      process.env.REACT_APP_AE_WIDGET_SRC ||
      "https://cdn.geo.elelem.ai/answer-engine/dev/script_latest.js";
    let scriptEl = document.querySelector(`script[src="${scriptUrl}"]`);
    const projectId = getLocalStorageWithExpiry("client_id") || "elelem";

    const aeBackendUrl = process.env.REACT_APP_AE_BACKEND_URL || "/ae-backend";

    if (!scriptEl) {
      scriptEl = document.createElement("script");
      scriptEl.src = scriptUrl;
      scriptEl.setAttribute("project-id", projectId);
      scriptEl.setAttribute("data-api-base", aeBackendUrl);
      scriptEl.setAttribute("apiBase", aeBackendUrl);
      scriptEl.setAttribute("apibase", aeBackendUrl);
      scriptEl.async = true;
      scriptEl.onload = () => {
        attachOverlayObserver();
      };
      scriptEl.onerror = () => {
        handleWidgetError();
      };
      document.body.appendChild(scriptEl);
    } else if (window.ElelemWidget) {
      try {
        window.ElelemWidget.mount({ projectId });
        attachOverlayObserver();
      } catch (err) {
        handleWidgetError();
      }
    }

    // Handle completed chat request events from the global index.js interceptor
    const handleChatCompleted = (event) => {
      const { requestPayload, responseData, questionTimestamp, answerTimestamp } = event.detail || {};
      if (responseData && responseData.success) {
        const reply = responseData.reply;
        const responseId = responseData.response_id;
        const chatSessionId = responseData.chat_session_id || requestPayload?.chat_session_id;
        const userMessage = requestPayload?.user_message;

        if (userMessage && reply && responseId) {
          handleInterceptedMessage({
            chat_session_id: chatSessionId,
            user_message: userMessage,
            reply: reply,
            response_id: responseId,
            question_timestamp: questionTimestamp,
            answer_timestamp: answerTimestamp,
          });
        }
      } else if (responseData && !responseData.success) {
        const errorMsg =
          responseData.error ||
          responseData.message ||
          (responseData.status ? `Request failed with status ${responseData.status}` : "Chat request failed.");
        showApiError(errorMsg);
      }
    };

    window.addEventListener("ae-widget:chat-request-completed", handleChatCompleted);

    // Origins that are permitted to post widget messages to this page.
    // Covers: the widget CDN, the answer-engine runtime (used by internal
    // iframes the widget may spawn), and the app's own origin (for messages
    // posted by the inline widget script running in page context).
    const TRUSTED_ORIGINS = new Set([
      "https://cdn.geo.elelem.ai",
      "https://general-runtime.answer-engine.elelem.ai",
      window.location.origin,
    ]);

    // Listen for the widget's internal close (X button inside the modal)
    const handleWidgetMessage = (event) => {
      // Reject messages from untrusted origins before touching any payload.
      if (!TRUSTED_ORIGINS.has(event.origin)) return;

      let data;
      try {
        data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch (e) {
        return;
      }

      console.debug("New event from widget:", data?.type);

      // Close/open events used for controlling preview state
      if (data?.type === "elelem-answer-engine:close" && isOpen) {
        isOpen = false;
        setIsChatOpen(false);
      } else if (data?.type === "elelem-answer-engine:open") {
        isOpen = true;
        setIsChatOpen(true);
      }
    };

    window.addEventListener("message", handleWidgetMessage);

    return () => {
      console.error = originalConsoleError;
      window.removeEventListener("error", handleWindowError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
      window.removeEventListener("ae-widget:chat-request-completed", handleChatCompleted);
      window.removeEventListener("message", handleWidgetMessage);
      if (overlayObserver) {
        overlayObserver.disconnect();
        overlayObserver = null;
      }
      if (window.ElelemWidget) {
        window.ElelemWidget.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleInterceptedMessage]);

  if (!PROJECT_ID) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 mt-4">
        <i
          className="bi bi-lock text-muted mb-3"
          style={{ fontSize: "48px" }}
        ></i>
        <h5 className="fw-semibold text-dark">
          {t(
            "answerEngine.sandboxNotReadyTitle",
            "Sandbox Project Not Ready",
          )}
        </h5>
        <p className="text-muted mt-2" style={{ maxWidth: "420px" }}>
          {t(
            "answerEngine.sandboxNotReadyMessage",
            "Answer Engine Sandbox has not been setup for your project yet. Please contact support.",
          )}
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="sandbox-container"
      className={`card card-radius shadow-sm border-0 overflow-hidden sandbox-container ${isChatOpen ? "chat-isFullscreen" : ""}`}
      style={{ height: "100%" }}
    >
      <div
        className="d-flex flex-row h-100 overflow-hidden"
        style={{ height: "100%" }}
      >
        {/* Website Preview Area */}
        <div className="flex-grow-1 position-relative bg-light">
          {widgetError && (
            <div
              className="position-absolute top-0 start-0 w-100 p-2 text-center small text-dark fw-semibold border-bottom shadow-sm d-flex align-items-center justify-content-center gap-2"
              style={{
                zIndex: 20,
                background: "#fff3cd",
                borderColor: "#ffeba8",
              }}
            >
              <Info size={16} className="text-warning flex-shrink-0" />
              <span>{widgetError}</span>
              <button
                type="button"
                data-testid="retry-widget-btn"
                className="btn btn-sm btn-outline-dark ms-2 d-inline-flex align-items-center gap-1 py-0 px-2"
                style={{ fontSize: "12px", lineHeight: "1.5" }}
                onClick={handleRetryWidget}
              >
                <RefreshCw size={12} />
                <span>{t("common.retry", "Retry")}</span>
              </button>
            </div>
          )}
          <div className="preview-wrapper h-100 position-relative">
            <div
              className={`position-absolute h-100 w-100 top-0 start-0 ${isChatOpen ? "bg-preview-fs" : ""}`}
            >
              <img
                src={PREVIEW_BKGD}
                alt="Website preview"
                className={`w-100 h-100 object-fit-cover ${isChatOpen ? "opacity-100" : "opacity-50"}`}
              />
            </div>
          </div>
        </div>

        {/* Feedback Panel Sidebar */}
        <div
          className="bg-white border-start position-relative"
          style={{ width: "320px" }}
        >
          <FeedbackPanel
            interactions={interactions}
            onFeedbackSaved={saveFeedback}
            onHistoryOpen={() => setIsHistoryOpen(true)}
            onReturnToLive={handleReturnToLive}
            isViewingHistory={isViewingHistory}
            loadError={conversationLoadError}
            isLoading={isInteractionsLoading}
          />
          <ConversationHistoryDrawer
            isOpen={isHistoryOpen}
            onClose={() => setIsHistoryOpen(false)}
            onSelectSession={loadConversation}
            currentSessionId={viewingSessionId}
          />
        </div>
      </div>
    </div>
  );
}
